import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatDateTime } from '../../lib/format.js';
import { StatusBadge, type StatusTone } from '../primitives/badge.js';

export interface TraceEvent {
  id: string;
  /** Step name or human label (e.g. "matched_lead", "Cita confirmada"). */
  title: React.ReactNode;
  /** ISO timestamp; formatted in `timezone`. */
  at?: string | null;
  detail?: React.ReactNode;
  /** Optional status badge (tone + visible text). */
  status?: { tone: StatusTone; label: string };
  /** Render the title in monospace (technical step ids). */
  code?: boolean;
  /** Expandable technical payload (args, results). Collapsed by default. */
  payload?: React.ReactNode;
  payloadLabel?: string;
}

export interface EventTraceTimelineProps {
  events: readonly TraceEvent[];
  /** Accessible name of the list, e.g. "Pasos del recibo". */
  label: string;
  /** IANA timezone for timestamps (workspace timezone). */
  timezone: string;
  emptyText?: string;
  className?: string;
}

/** Ordered event/step trace (receipts, appointment history, routine steps, tool calls) with a left rail. */
export function EventTraceTimeline({
  events,
  label,
  timezone,
  emptyText = 'Sin pasos registrados.',
  className,
}: EventTraceTimelineProps) {
  if (events.length === 0)
    return (
      <p className={cn('text-body text-text-secondary', className)}>
        {emptyText}
      </p>
    );
  return (
    <ol
      aria-label={label}
      className={cn(
        'grid gap-3 border-l-2 border-border-subtle pl-4',
        className,
      )}
    >
      {events.map((e) => (
        <li key={e.id} className="relative grid min-w-0 gap-0.5">
          <span
            aria-hidden
            className="absolute -left-[1.4rem] top-1.5 size-2.5 rounded-pill border-2 border-surface-default bg-border-control"
          />
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span
              className={cn(
                'min-w-0 [overflow-wrap:anywhere] text-text-primary',
                e.code ? 'font-mono text-meta' : 'text-body font-medium',
              )}
            >
              {e.title}
            </span>
            {e.status ? (
              <StatusBadge tone={e.status.tone}>{e.status.label}</StatusBadge>
            ) : null}
          </div>
          {e.detail ? (
            <div className="text-body text-text-secondary">{e.detail}</div>
          ) : null}
          {e.at ? (
            <time
              dateTime={e.at}
              className="ef-tabular text-meta text-text-secondary"
            >
              {formatDateTime(e.at, timezone)}
            </time>
          ) : null}
          {e.payload ? (
            <details className="mt-1">
              <summary className="cursor-pointer rounded-control text-meta font-medium text-brand-primary focus-visible:outline-2 focus-visible:outline-focus">
                {e.payloadLabel ?? 'Ver detalle técnico'}
              </summary>
              <div className="mt-1 max-h-64 overflow-auto rounded-control bg-surface-subtle p-2 font-mono text-meta text-text-primary">
                {e.payload}
              </div>
            </details>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
